import attachAddressAutocomplete from 'foundation/js/address/addressAutocomplete'
import { isAddressServiceAvailable } from 'foundation/js/address/addressLookup'
import { onBeforeUnmount, onMounted, ref, watchEffect } from 'vue'

let instanceCount = 0

export default {
    name: 'FormAddressField',
    props: {
        addressLineValue: {
            type: String,
            required: true
        },
        zipCodeValue: {
            type: String,
            required: true
        },
        cityValue: {
            type: String,
            required: true
        },
        addressPlaceholder: {
            type: String,
            required: false
        },
        zipCodePlaceholder: {
            type: String,
            required: false
        },
        cityPlaceholder: {
            type: String,
            required: false
        },
        addressLabel: {
            type: String,
            required: false,
            default: 'Adresse og husnummer'
        },
        zipCodeLabel: {
            type: String,
            required: false,
            default: 'Postnummer'
        },
        cityLabel: {
            type: String,
            required: false,
            default: 'By'
        },
        errorMessageLabel: {
            type: String,
            required: true
        },
        isValidationError: {
            type: Boolean,
            required: true
        },
        onchangeCallback: {
            type: Function,
            required: false
        },
        onclickCallback: {
            type: Function,
            required: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        isRequired: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    setup(props, { emit }) {
        const addressLine = ref(props.addressLineValue ?? '')
        const zipCode = ref(props.zipCodeValue ?? '')
        const city = ref(props.cityValue ?? '')
        const addressServiceUnavailable = ref(false)

        // A page can hold more than one address field so ids have to be unique
        instanceCount += 1
        const addressLineId = `kb-address-line-${instanceCount}`
        const zipCodeId = `kb-address-zipcode-${instanceCount}`
        const cityId = `kb-address-city-${instanceCount}`

        const addressLineInput = ref(null)
        let detachAutocomplete

        watchEffect(() => {
            emit('update:addressLineValue', addressLine.value)
            emit('update:zipCodeValue', zipCode.value)
            emit('update:cityValue', city.value)
        })

        // Typing in the address line makes the picked address stale, so
        // postnummer and by are dropped - but the text is left alone, so an
        // address can be corrected without starting over.
        const clearSelectedAddress = function () {
            zipCode.value = ''
            city.value = ''
        }

        onMounted(async () => {
            addressServiceUnavailable.value = !(await isAddressServiceAvailable())

            if (addressServiceUnavailable.value) {
                return
            }

            detachAutocomplete = attachAddressAutocomplete(
                addressLineInput.value,
                {
                    onSelect: (address) => {
                        addressLine.value = address.addressLine
                        zipCode.value = address.zipCode
                        city.value = address.city

                        emit('update:addressSelected', {
                            addressLine: addressLine.value,
                            zipCode: zipCode.value,
                            city: city.value
                        })
                    },
                    onError: () => {
                        addressServiceUnavailable.value = true
                    }
                }
            )
        })

        onBeforeUnmount(() => {
            if (detachAutocomplete) {
                detachAutocomplete()
            }
        })

        return {
            addressLine,
            zipCode,
            city,
            clearSelectedAddress,
            addressServiceUnavailable,
            addressLineId,
            zipCodeId,
            cityId,
            addressLineInput
        }
    }
}
